import { injectable, inject } from 'inversify';
import { PackageMapping, PackageType, MappingStatus, PackageAnalysis } from '../domain/entities/PackageMapping';
import type { IPackageMappingRepository, PackageMappingFilters } from '../domain/ports/types';
import { IPackageMappingRepositorySymbol } from '../domain/ports/symbols';
import { 
    CreatePackageMappingRequest, 
    UpdatePackageMappingRequest, 
    BulkPackageMappingRequest,
    GetPackageMappingsQuery 
} from '../interface/dto/PackageMappingRequestDTOs';
import { DomainDesignerError } from '../../strategic-design/domain/errors/DomainDesignerErrors';

/**
 * Package Mapping Application Service
 * 
 * Orchestrates package-to-domain mapping operations.
 * Handles business workflows while maintaining domain integrity.
 */
@injectable()
export class PackageMappingApplicationService {
    constructor(
        @inject(IPackageMappingRepositorySymbol)
        private readonly packageMappingRepository: IPackageMappingRepository
    ) {}

    /**
     * Get package mappings with filtering and pagination
     */
    async getPackageMappings(query: GetPackageMappingsQuery): Promise<{
        mappings: PackageMapping[];
        total: number;
    }> {
        const filters: PackageMappingFilters = {
            domainId: query.domainId,
            status: query.status,
            packageType: query.packageType,
            search: query.search,
            limit: query.limit,
            offset: query.offset
        };

        return await this.packageMappingRepository.findAll(filters);
    }

    /**
     * Get specific package mapping
     */
    async getPackageMapping(packageName: string): Promise<PackageMapping | null> {
        return await this.packageMappingRepository.findByPackageName(packageName);
    }

    /**
     * Create a new package mapping
     */
    async createPackageMapping(request: CreatePackageMappingRequest): Promise<PackageMapping> {
        // Check if package mapping already exists
        const existing = await this.packageMappingRepository.findByPackageName(request.packageName);
        if (existing) {
            throw new DomainDesignerError(
                `Package mapping already exists: ${request.packageName}`,
                'PACKAGE_MAPPING_EXISTS'
            );
        }

        // Create classification object
        const classification = {
            confidence: request.confidence || 0.5,
            reasoning: request.reasoning || 'Manual classification',
            suggestedDomain: request.domainId || null,
            automaticallyClassified: request.automaticallyClassified
        };

        // Create package mapping entity
        const mapping = PackageMapping.create(
            request.packageName,
            request.packageType as PackageType,
            request.domainId,
            classification
        );

        // Add notes if provided
        if (request.notes) {
            mapping.addNotes(request.notes);
        }

        // Save to repository
        await this.packageMappingRepository.save(mapping);

        return mapping;
    }

    /**
     * Update an existing package mapping
     */
    async updatePackageMapping(
        packageName: string, 
        request: UpdatePackageMappingRequest
    ): Promise<PackageMapping> {
        // Find existing mapping
        const mapping = await this.packageMappingRepository.findByPackageName(packageName);
        if (!mapping) {
            throw new DomainDesignerError(
                `Package mapping not found: ${packageName}`,
                'PACKAGE_MAPPING_NOT_FOUND'
            );
        }

        // Apply updates
        if (request.domainId) {
            mapping.assignToDomain(request.domainId, request.reviewedBy);
        }

        if (request.status) {
            switch (request.status) {
                case MappingStatus.ORPHANED:
                    mapping.markAsOrphaned();
                    break;
                case MappingStatus.NEEDS_REVIEW:
                    mapping.markForReview(request.notes);
                    break;
                case MappingStatus.DISPUTED:
                    if (!request.notes) {
                        throw new DomainDesignerError(
                            'Notes are required when marking as disputed',
                            'NOTES_REQUIRED'
                        );
                    }
                    mapping.markAsDisputed(request.notes);
                    break;
            }
        }

        if (request.notes) {
            mapping.addNotes(request.notes, request.reviewedBy);
        }

        if (request.confidence !== undefined || request.reasoning) {
            const currentClassification = mapping.classification;
            const updatedClassification = {
                ...currentClassification,
                confidence: request.confidence ?? currentClassification.confidence,
                reasoning: request.reasoning ?? currentClassification.reasoning
            };
            mapping.updateClassification(updatedClassification);
        }

        // Save updated mapping
        await this.packageMappingRepository.save(mapping);

        return mapping;
    }

    /**
     * Bulk update package mappings
     */
    async bulkUpdatePackageMappings(request: BulkPackageMappingRequest): Promise<{
        updated: number;
        errors: Array<{ packageName: string; error: string }>;
    }> {
        const results = {
            updated: 0,
            errors: [] as Array<{ packageName: string; error: string }>
        };

        for (const mapping of request.mappings) {
            try {
                // Find or create package mapping
                let packageMapping = await this.packageMappingRepository.findByPackageName(mapping.packageName);
                
                if (!packageMapping) {
                    // Create new mapping if it doesn't exist
                    packageMapping = PackageMapping.create(
                        mapping.packageName,
                        this.inferPackageType(mapping.packageName),
                        mapping.domainId,
                        {
                            confidence: 0.8,
                            reasoning: 'Bulk assignment',
                            suggestedDomain: mapping.domainId,
                            automaticallyClassified: true
                        }
                    );
                } else {
                    // Update existing mapping
                    packageMapping.assignToDomain(mapping.domainId, 'bulk-update');
                }

                await this.packageMappingRepository.save(packageMapping);
                results.updated++;
            } catch (error) {
                results.errors.push({
                    packageName: mapping.packageName,
                    error: error instanceof Error ? error.message : 'Unknown error'
                });
            }
        }

        return results;
    }

    /**
     * Analyze package for domain suggestions
     */
    async analyzePackage(packageName: string): Promise<PackageAnalysis> {
        // For now, return a basic analysis
        // TODO: Integrate with AI classification service
        const packageType = this.inferPackageType(packageName);
        const suggestedDomains = await this.generateDomainSuggestions(packageName, packageType);

        return {
            packageName,
            suggestedDomains,
            packageType,
            complexity: this.inferComplexity(packageName),
            recommendations: this.generateRecommendations(packageName, packageType)
        };
    }

    /**
     * Delete a package mapping
     */
    async deletePackageMapping(packageName: string): Promise<void> {
        const exists = await this.packageMappingRepository.exists(packageName);
        if (!exists) {
            throw new DomainDesignerError(
                `Package mapping not found: ${packageName}`,
                'PACKAGE_MAPPING_NOT_FOUND'
            );
        }

        await this.packageMappingRepository.deleteByPackageName(packageName);
    }

    /**
     * Get package mapping statistics
     */
    async getStatistics() {
        return await this.packageMappingRepository.getStatistics();
    }

    // Private helper methods
    private inferPackageType(packageName: string): PackageType {
        if (packageName.includes('-ui') || packageName.includes('frontend')) {
            return PackageType.APP;
        }
        if (packageName.includes('-api')) {
            return PackageType.API;
        }
        if (packageName.includes('-service') || packageName.includes('backend')) {
            return PackageType.SERVICE;
        }
        if (packageName.includes('shared-')) {
            return PackageType.SHARED;
        }
        if (packageName.includes('domain')) {
            return PackageType.DOMAIN;
        }
        return PackageType.SERVICE; // Default
    }

    private async generateDomainSuggestions(packageName: string, packageType: PackageType): Promise<Array<{
        domainId: string;
        domainName: string;
        confidence: number;
        reasoning: string;
    }>> {
        // Simple rule-based suggestions for now
        // TODO: Replace with AI-powered classification
        
        const suggestions = [];
        
        if (packageName.includes('auth') || packageName.includes('iam')) {
            suggestions.push({
                domainId: 'cb5816f9-768d-483b-81f0-fc3a3ebb02e8',
                domainName: 'Authentication & Identity',
                confidence: 0.9,
                reasoning: 'Package name contains authentication-related terms'
            });
        }
        
        if (packageName.includes('dev') || packageName.includes('tool') || packageName.includes('test')) {
            suggestions.push({
                domainId: '34841396-a577-4d0b-9ac3-aa7073062c0f',
                domainName: 'Software Development',
                confidence: 0.85,
                reasoning: 'Package appears to be development-related tooling'
            });
        }
        
        if (packageName.includes('ai') || packageName.includes('product')) {
            suggestions.push({
                domainId: '1790acd6-c89a-4c90-b1d1-866d2f7fb7bf',
                domainName: 'Product Development Platform',
                confidence: 0.8,
                reasoning: 'Package relates to AI or product development'
            });
        }

        if (packageName.includes('database') || packageName.includes('mongo') || packageName.includes('ops')) {
            suggestions.push({
                domainId: 'b5a7fd2d-6386-454d-82cf-6754bac5a041',
                domainName: 'Dev Ops',
                confidence: 0.8,
                reasoning: 'Package appears to be infrastructure/operations related'
            });
        }
        
        // Default suggestion if no specific patterns match
        if (suggestions.length === 0) {
            suggestions.push({
                domainId: '34841396-a577-4d0b-9ac3-aa7073062c0f',
                domainName: 'Software Development',
                confidence: 0.5,
                reasoning: 'Default classification - requires manual review'
            });
        }
        
        return suggestions;
    }

    private inferComplexity(packageName: string): 'low' | 'medium' | 'high' {
        if (packageName.includes('shared') || packageName.includes('util')) {
            return 'low';
        }
        if (packageName.includes('ai') || packageName.includes('complex')) {
            return 'high';
        }
        return 'medium';
    }

    private generateRecommendations(packageName: string, packageType: PackageType): string[] {
        const recommendations = [];
        
        if (packageType === PackageType.APP) {
            recommendations.push('Consider separating UI concerns from business logic');
        }
        
        if (packageName.includes('shared')) {
            recommendations.push('Ensure shared packages follow Clean Architecture principles');
        }
        
        if (packageType === PackageType.SERVICE) {
            recommendations.push('Verify service boundaries align with domain boundaries');
        }
        
        return recommendations;
    }
}